import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageProvider';
import { translations } from '../i18n/translations';

const ServicesPage: React.FC = () => {
  const { language } = useLanguage();
  const dict = translations[language].servicesPage;

  const services = [
    {
      title: dict.items.s1.title,
      description: dict.items.s1.description,
      details: dict.items.s1.details,
      features: dict.items.s1.features,
      image: "/images/services/automation.jpg",
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      )
    },
    {
      title: dict.items.s2.title,
      description: dict.items.s2.description,
      details: dict.items.s2.details,
      features: dict.items.s2.features,
      image: "/images/services/tailored.jpg",
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
      )
    },
    {
      title: dict.items.s3.title,
      description: dict.items.s3.description,
      details: dict.items.s3.details,
      features: dict.items.s3.features,
      image: "/images/services/data_processing.jpg",
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
        </svg>
      )
    },
    {
      title: dict.items.s4.title,
      description: dict.items.s4.description,
      details: dict.items.s4.details,
      features: dict.items.s4.features,
      image: "/images/services/privacy.jpg",
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      )
    },
    {
      title: dict.items.s5.title,
      description: dict.items.s5.description,
      details: dict.items.s5.details,
      features: dict.items.s5.features,
      image: "/images/services/intergration.jpg",
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      )
    }
  ];

  return (
    <div className="py-16 md:py-24">
      {/* Hero Section */}
      <section className="bg-sand py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-6xl font-bold text-navy mb-6 font-space">
              {dict.heroTitle}
            </h1>
            <p className="text-xl md:text-2xl text-slate mb-8 leading-relaxed">
              {dict.heroSub}
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Intro Text */}
          <div className="text-center mb-16">
            <p className="text-xl text-slate max-w-4xl mx-auto leading-relaxed">
              {dict.introText}
            </p>
          </div>
          <div className="space-y-16">
            {services.map((service, index) => (
              <div key={index} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''}`}>
                <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                  <div className="flex items-center mb-6">
                    <div className="p-4 bg-gradient-to-br from-sky/20 to-sky/5 rounded-full text-navy mr-4 shadow-sm">
                      {service.icon}
                    </div>
                    <h2 className="text-3xl font-bold text-navy font-space">
                      {service.title}
                    </h2>
                  </div>
                  
                  <p className="text-xl text-slate mb-6 leading-relaxed">
                    {service.details}
                  </p>
                  
                  <div className="space-y-3">
                    {service.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-center">
                        <svg className="w-5 h-5 text-sky mr-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span className="text-slate">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className={`bg-sand rounded-xl p-8 ${index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''}`}>
                  <div className="aspect-square bg-navy/5 rounded-lg overflow-hidden">
                    <img 
                      src={service.image} 
                      alt={service.title} 
                      className="w-full h-full object-cover transition-transform duration-500 hover:scale-105" 
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-navy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-5xl font-bold text-sand mb-6 font-space">
            {dict.ctaTitle}
          </h2>
          <p className="text-xl text-sand/80 mb-8 max-w-3xl mx-auto">
            {dict.ctaSub}
          </p>
          <Link 
            to="/contact#calendly"
            className="bg-sky text-navy px-8 py-4 rounded-lg font-semibold hover:bg-sky/90 transition-colors duration-200 inline-block"
          >
            {dict.ctaButton}
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
